import { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';

export function useLanguage() {
  const [isSpanish, setIsSpanishState] = useState(() => {
    const cookieValue = Cookies.get('isSpanish');
    return cookieValue === undefined ? true : cookieValue === 'true';
  });

  // Update cookie and state
  const setIsSpanish = useCallback((value) => {
    setIsSpanishState(value);
    Cookies.set('isSpanish', value, { expires: 365 });
    // Also update localStorage to trigger storage event for other tabs/components
    localStorage.setItem('isSpanish', value);
  }, []);

  // Listen for changes in localStorage (cross-tab sync)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'isSpanish') {
        setIsSpanishState(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Sync with cookie if changed externally
  useEffect(() => {
    const interval = setInterval(() => {
      const cookieValue = Cookies.get('isSpanish');
      setIsSpanishState(prev => {
        const cookieBool = cookieValue === undefined ? true : cookieValue === 'true';
        return prev !== cookieBool ? cookieBool : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return [isSpanish, setIsSpanish];
}

export const translations = {
  welcome: {
    eng: 'Welcome',
    esp: 'Bienvenido',
  },
  streamline: {
    eng: 'Streamline your customs declarations and document processing',
    esp: 'Optimiza tus declaraciones de aduana y el procesamiento de documentos',
  },
  confirmAndProcess: {
    eng: 'Confirm and Process Documents',
    esp: 'Confirmar y Procesar Documentos',
  },
  reset: {
    eng: 'Reset',
    esp: 'Reiniciar',
  },
  generatingDeclaration: {
    eng: 'Generating Declaration...',
    esp: 'Generando Declaración...',
  },
  retryDeclaration: {
    eng: 'Retry Declaration Generation',
    esp: 'Reintentar Generación de Declaración',
  },
  generateDeclaration: {
    eng: 'Generate Declaration',
    esp: 'Generar Declaración',
  },
  noCreditsLeft: {
    eng: 'You have used up all your remaining credits!',
    esp: '¡Has usado todos tus créditos restantes!'
  },
  pleaseBuyCredits: {
    eng: 'Please buy more credits to continue using the service.',
    esp: 'Por favor, compra más créditos para continuar usando el servicio.'
  },
  buyCredits: { eng: 'Buy Credits', esp: 'Comprar créditos' },
  buy: { eng: 'Buy', esp: 'Comprar' },
  restore: { eng: 'Restore Processed Data', esp: 'Restaurar Datos Procesados' },
  declarationType: { eng: 'Declaration type', esp: 'Tipo de declaración' },
  export: { eng: 'Export', esp: 'Exportación' },
  import: { eng: 'Import', esp: 'Importación' },
  declarationTypeLockedHint: {
    eng: 'The declaration type is fixed once processing starts.',
    esp: 'El tipo de declaración se fija una vez que comienza el procesamiento.',
  },
  // Add more as needed
};

export function getTranslation(key, isSpanish) {
  if (!translations[key]) return key;
  return isSpanish ? translations[key].esp : translations[key].eng;
}

export const declarationSummaryTranslations = {
  declarationSummary: {
    eng: 'Declaration Summary',
    esp: 'Resumen de Declaración',
  },
  save: {
    eng: 'Save Changes',
    esp: 'Guardar cambios',
  },
  cancel: {
    eng: 'Cancel Changes',
    esp: 'Cancelar cambios',
  },
  totalDeclarations: {
    eng: 'Total Declarations',
    esp: 'Total Declaraciones',
  },
  itemsCount: {
    eng: 'Items Count',
    esp: 'Número de Partidas',
  },
  export: {
    eng: 'Export',
    esp: 'Expediente',
  },
  import: {
    eng: 'Import',
    esp: 'Importación',
  },
  enterDeclaration: {
    eng: 'Enter Declaration',
    esp: 'Entrada Expediente',
  },
  items: {
    eng: 'Items',
    esp: 'Partidas',
  },
  addItem: {
    eng: 'Add Item',
    esp: 'Agregar Partida',
  },
  item: {
    eng: 'Item',
    esp: 'Partida',
  },
  saveAllDocuments: {
    eng: 'Apply to All',
    esp: 'Aplicar a Todos',
  },
  saveAllDocumentsTooltip: {
    eng: 'Apply this value to all documents in all items of this declaration',
    esp: 'Aplicar este valor a todos los documentos en todas las partidas de este expediente',
  }
  // Add more as needed
};

export function getDeclarationSummaryTranslation(key, isSpanish) {
  if (!declarationSummaryTranslations[key]) return key;
  return isSpanish ? declarationSummaryTranslations[key].esp : declarationSummaryTranslations[key].eng;
}

export const reviewUserDetailsTranslations = {
  reviewDetails: { eng: 'Review Details', esp: 'Revisar Detalles' },
  contactsFilterAll: { eng: 'All', esp: 'Todos' },
  contactsFilterSenders: { eng: 'Senders', esp: 'Remitentes' },
  contactsFilterReceivers: { eng: 'Receivers', esp: 'Destinatarios' },
  contactsBadgeSender: { eng: 'Sender', esp: 'Remitente' },
  contactsBadgeReceiver: { eng: 'Receiver', esp: 'Destinatario' },
  noContactsForModalFilter: {
    eng: 'No contacts match this filter. Try another filter or search.',
    esp: 'Ningún contacto coincide con este filtro. Prueba otro filtro o la búsqueda.',
  },
  company: { eng: 'Company', esp: 'Empresa' },
  update: { eng: 'Update', esp: 'Actualizar' },
  companyName: { eng: 'Company Name', esp: 'Nombre de la Empresa' },
  companyEmail: { eng: 'Company Email', esp: 'Correo de la Empresa' },
  nif: { eng: 'NIF', esp: 'NIF' },
  eoriNumber: { eng: 'EORI Number', esp: 'Número EORI' },
  address: { eng: 'Address', esp: 'Dirección' },
  city: { eng: 'City', esp: 'Ciudad' },
  postalCode: { eng: 'Postal Code', esp: 'Código Postal' },
  country: { eng: 'Country', esp: 'País' },
  standardCustomsOffice: { eng: 'Standard Customs Office', esp: 'Aduana Habitual' },
  identificacion_fiscal: { eng: 'Identification Fiscal', esp: 'Identificación Fiscal' },
};
export function getReviewUserDetailsTranslation(key, isSpanish) {
  if (!reviewUserDetailsTranslations[key]) return key;
  return isSpanish ? reviewUserDetailsTranslations[key].esp : reviewUserDetailsTranslations[key].eng;
}

export const reviewSenderDetailsTranslations = {
  sender: { eng: 'Exporter (Sender)', esp: ' Exportador (Remitente)' },
  selectSender: { eng: 'Select Exporter (Sender)', esp: 'Seleccionar Exportador (Remitente)' },
  senderName: { eng: 'Exporter Name', esp: 'Nombre del Exportador' },
  senderTaxId: { eng: 'Exporter Tax ID', esp: 'NIF del Exportador' },
  senderEori: { eng: 'Exporter EORI', esp: 'EORI del Exportador' },
  senderAddress: { eng: 'Exporter Address', esp: 'Dirección del Exportador' },
  senderCity: { eng: 'Exporter City', esp: 'Ciudad del Exportador' },
  senderCountry: { eng: 'Exporter Country', esp: 'País del Exportador' },
  searchSenders: { eng: 'Search exporters...', esp: 'Buscar exportadores...' },
  sortBy: { eng: 'Sort by', esp: 'Ordenar por' },
  idAsc: { eng: 'ID (low to high)', esp: 'ID (menor a mayor)' },
  idDesc: { eng: 'ID (high to low)', esp: 'ID (mayor a menor)' },
  nameAZ: { eng: 'Name (A-Z)', esp: 'Nombre (A-Z)' },
  nameZA: { eng: 'Name (Z-A)', esp: 'Nombre (Z-A)' },
  newestFirst: { eng: 'Newest First', esp: 'Más nuevos primero' },
  oldestFirst: { eng: 'Oldest First', esp: 'Más antiguos primero' },
  noSendersFound: { eng: 'No exporters found matching', esp: 'No se encontraron exportadores que coincidan con' },
  noSendersAvailable: { eng: 'No Exporters Available', esp: 'No hay exportadores disponibles' },
  addSender: { eng: 'Add Exporter', esp: 'Agregar Exportador' },
  suggestedSenders: { eng: 'Suggested Exporters', esp: 'Exportadores sugeridos' },
};
export function getReviewSenderDetailsTranslation(key, isSpanish) {
  if (!reviewSenderDetailsTranslations[key]) return key;
  return isSpanish ? reviewSenderDetailsTranslations[key].esp : reviewSenderDetailsTranslations[key].eng;
}

export const reviewReceiverDetailsTranslations = {
  receiver: { eng: 'Importer (Receiver)', esp: ' Importador (Destinario)' },
  selectReceiver: { eng: 'Select Importer (Receiver)', esp: 'Seleccionar Importador (Destinatario) ' },
  receiverName: { eng: 'Importer Name', esp: 'Nombre del Importador' },
  receiverTaxId: { eng: 'Importer Tax ID', esp: 'NIF del Importador' },
  receiverEori: { eng: 'Importer EORI', esp: 'EORI del Importador' },
  receiverAddress: { eng: 'Importer Address', esp: 'Dirección del Importador' },
  receiverCity: { eng: 'Importer City', esp: 'Ciudad del Importador' },
  receiverCountry: { eng: 'Importer Country', esp: 'País del Importador' },
  searchReceivers: { eng: 'Search importers...', esp: 'Buscar importadores...' },
  sortBy: { eng: 'Sort by', esp: 'Ordenar por' },
  idAsc: { eng: 'ID (low to high)', esp: 'ID (menor a mayor)' },
  idDesc: { eng: 'ID (high to low)', esp: 'ID (mayor a menor)' },
  nameAZ: { eng: 'Name (A-Z)', esp: 'Nombre (A-Z)' },
  nameZA: { eng: 'Name (Z-A)', esp: 'Nombre (Z-A)' },
  newestFirst: { eng: 'Newest First', esp: 'Más nuevos primero' },
  oldestFirst: { eng: 'Oldest First', esp: 'Más antiguos primero' },
  noReceiversFound: { eng: 'No importers found matching', esp: 'No se encontraron importadores que coincidan con' },
  noReceiversAvailable: { eng: 'No Importers Available', esp: 'No hay importadores disponibles' },
  addReceiver: { eng: 'Add Importer', esp: 'Agregar Importador' },
  suggestedReceivers: { eng: 'Suggested Importers', esp: 'Importadores sugeridos' },
};
export function getReviewReceiverDetailsTranslation(key, isSpanish) {
  if (!reviewReceiverDetailsTranslations[key]) return key;
  return isSpanish ? reviewReceiverDetailsTranslations[key].esp : reviewReceiverDetailsTranslations[key].eng;
}

export const reviewAgentsTranslations = {
  agent: { eng: 'Agent', esp: 'Agente' },
  optional: { eng: '(optional)', esp: '(opcional)' },
  selectAgent: { eng: 'Select Agent', esp: 'Seleccionar Agente' },
  agentName: { eng: 'Agent Name', esp: 'Nombre del Agente' },
  agentTaxId: { eng: 'Agent Tax ID', esp: 'NIF del Agente' },
  agentEori: { eng: 'Agent EORI', esp: 'EORI del Agente' },
  agentAddress: { eng: 'Agent Address', esp: 'Dirección del Agente' },
  agentCity: { eng: 'Agent City', esp: 'Ciudad del Agente' },
  agentCountry: { eng: 'Agent Country', esp: 'País del Agente' },
  searchAgents: { eng: 'Search agents...', esp: 'Buscar agentes...' },
  sortBy: { eng: 'Sort by', esp: 'Ordenar por' },
  nameAZ: { eng: 'Name (A-Z)', esp: 'Nombre (A-Z)' },
  nameZA: { eng: 'Name (Z-A)', esp: 'Nombre (Z-A)' },
  newestFirst: { eng: 'Newest First', esp: 'Más nuevos primero' },
  oldestFirst: { eng: 'Oldest First', esp: 'Más antiguos primero' },
  noAgentsFound: { eng: 'No agents found matching', esp: 'No se encontraron agentes que coincidan con' },
  noAgentsAvailable: { eng: 'No Agents Available', esp: 'No hay agentes disponibles' },
  addAgent: { eng: 'Add Agent', esp: 'Agregar Agente' },
  suggestedAgents: { eng: 'Suggested Agents', esp: 'Agentes sugeridos' },
  refreshList: { eng: 'Refresh list', esp: 'Actualizar lista' },
  refreshingList: { eng: 'Refreshing...', esp: 'Actualizando...' },
};
export function getReviewAgentsTranslation(key, isSpanish) {
  if (!reviewAgentsTranslations[key]) return key;
  return isSpanish ? reviewAgentsTranslations[key].esp : reviewAgentsTranslations[key].eng;
}

export const finalJsonDataTranslations = {
  reviewFinalJson: { eng: 'Review Final JSON file', esp: 'Revisar archivo JSON final' },
  waitingForData: { eng: 'Waiting for data to be generated...', esp: 'Esperando a que se genere la información...' },
};
export function getFinalJsonDataTranslation(key, isSpanish) {
  if (!finalJsonDataTranslations[key]) return key;
  return isSpanish ? finalJsonDataTranslations[key].esp : finalJsonDataTranslations[key].eng;
}

export const processingModalTranslations = {
  addingFiles: { eng: 'Adding files to processing queue...', esp: 'Agregando archivos a la cola de procesamiento...' },
  processingDocuments: { eng: 'Processing documents...', esp: 'Procesando documentos...' },
  processingComplete: { eng: 'Processing complete.', esp: 'Procesamiento completo.' },
  processingError: { eng: 'Processing Error', esp: 'Error de procesamiento' },
  documentsProcessed: { eng: 'Documents Processed', esp: 'Documentos procesados' },
  processingDocumentsTitle: { eng: 'Processing Documents...', esp: 'Procesando documentos...' },
  failedToStart: { eng: 'Failed to start document processing', esp: 'No se pudo iniciar el procesamiento de documentos' },
  retryProcessing: { eng: 'Retry Processing', esp: 'Reintentar procesamiento' },
  retrying: { eng: 'Retrying...', esp: 'Reintentando...' },
  dismissError: { eng: 'Dismiss', esp: 'Descartar' },
  processingFailedHint: {
    eng: 'Your uploaded PDFs are still available. You can adjust them and try again.',
    esp: 'Tus PDFs siguen disponibles. Puedes revisarlos e intentarlo de nuevo.',
  },
  processingDone: { eng: 'Processing Done', esp: 'Procesamiento Completado' },
  processingDoneNotification: { eng: 'Document processing is complete!', esp: '¡El procesamiento de documentos está completo!' },
};
export function getProcessingModalTranslation(key, isSpanish) {
  if (!processingModalTranslations[key]) return key;
  return isSpanish ? processingModalTranslations[key].esp : processingModalTranslations[key].eng;
}

export const generatedSuccessfullyModalTranslations = {
  generationFailed: { eng: 'Generation Failed', esp: 'Generación fallida' },
  taricDeclarationSuccess: { eng: 'Taric Declaration Generated Successfully', esp: 'Declaración Taric generada con éxito' },
  closeToCheckErrors: { eng: 'Close to check errors.', esp: 'Cerrar para revisar los errores.' },
  yourDeclarationSaved: { eng: 'Your customs declaration has been created and saved to the database. The JSON file is now ready for import into Taric DUASQL.', esp: 'Su declaración de aduana ha sido creada y guardada en la base de datos. El archivo JSON está listo para importar en Taric DUASQL.' },
};
export function getGeneratedSuccessfullyModalTranslation(key, isSpanish) {
  if (!generatedSuccessfullyModalTranslations[key]) return key;
  return isSpanish ? generatedSuccessfullyModalTranslations[key].esp : generatedSuccessfullyModalTranslations[key].eng;
}

export const taricJsonErrorsTranslations = {
  commonErrors: { eng: 'Common Errors', esp: 'Errores Comunes' },
  clientErrors: { eng: 'Client Errors', esp: 'Errores de Cliente' },
  agentErrors: { eng: 'Agent Errors', esp: 'Errores de Agente' },
  exportErrors: { eng: 'Export Errors', esp: 'Errores de Exportación' },
  export: { eng: 'Export', esp: 'Exportación' },
  item: { eng: 'Item', esp: 'Partida' },
  package: { eng: 'Package', esp: 'Embalaje' },
  document: { eng: 'Document', esp: 'Documento' },
  taricGenerationError: { eng: 'TARIC Generation Error', esp: 'Error de Generación TARIC' },
};
export function getTaricJsonErrorsTranslation(key, isSpanish) {
  if (!taricJsonErrorsTranslations[key]) return key;
  return isSpanish ? taricJsonErrorsTranslations[key].esp : taricJsonErrorsTranslations[key].eng;
}

export const mainTranslations = {
  reset: { eng: 'Reset', esp: 'Reiniciar' },
  resetApp: { eng: 'Reset App State', esp: 'Reiniciar Estado de la App' },
  resetAppConfirm: { 
    eng: 'Are you sure you want to reset all data? This will clear all documents, extracted data, and session data except your login credentials.', 
    esp: '¿Estás seguro de que quieres reiniciar todos los datos? Esto borrará todos los documentos, datos extraídos y datos de sesión excepto tus credenciales de inicio de sesión.' 
  },
  resetAppSuccess: { 
    eng: 'App state reset successfully', 
    esp: 'Estado de la app reiniciado exitosamente' 
  },
  logout: { eng: 'Logout', esp: 'Cerrar sesión' },
  preferences: { eng: 'Preferences', esp: 'Preferencias' },
  loadingYourData: { eng: 'Loading your data...', esp: 'Cargando tus datos...' },
  loadingPdfs: { eng: 'Loading PDFs...', esp: 'Cargando PDFs...' },
  goBackToHome: { eng: 'Go back to Home', esp: 'Volver al inicio' },
  esp: { eng: 'ESP', esp: 'ESP' },
  eng: { eng: 'ENG', esp: 'ING' },
  successfullyLoggedOut: { eng: 'Successfully logged out!', esp: '¡Sesión cerrada con éxito!' },
  noExpirationTime: { eng: 'No expiration time found for token', esp: 'No se encontró tiempo de expiración para el token' },
  profile: { eng: 'Profile', esp: 'Perfil' },
  errors: { eng: 'Errors', esp: 'Errores' },
  fieldOverview: { eng: 'Field Overview', esp: 'Resumen de Campos' },
  suggested: { eng: 'AI Suggestions', esp: 'Sugerencias de AI' },
  missingFields: { eng: 'Missing Fields', esp: 'Campos Faltantes' },
  nonMissingFields: { eng: 'Non-Missing Fields', esp: 'Campos Completados' },
  allFields: { eng: 'All Fields', esp: 'Todos los Campos' },
  fieldsWithoutInputs: { eng: 'Fields Without Inputs', esp: 'Campos Sin Entradas' },
  confidenceFields: { eng: 'Confidence Fields', esp: 'Campos de Confianza' },
  fixAllRequired: { eng: 'Fix all required fields before generating Taric JSON', esp: 'Corrige todos los campos obligatorios antes de generar el JSON de Taric' },
  fixAllRequiredBeforeSave: { eng: 'Fix all required fields before saving changes', esp: 'Corrige todos los campos obligatorios antes de guardar los cambios' },
  changesSaved: { eng: 'Changes saved successfully', esp: 'Cambios guardados correctamente' },
  changesCancelled: { eng: 'Changes discarded', esp: 'Cambios descartados' },
  aiSuggestionChanged: { eng: 'Changed', esp: 'Cambiado' },
  aiSuggestionRemoved: { eng: 'Removed', esp: 'Eliminado' },
  aiSuggestionAccept: { eng: 'Accept', esp: 'Aceptar' },
  aiSuggestionReject: { eng: 'Reject', esp: 'Rechazar' },
  aiSuggestionRetry: { eng: 'Revert', esp: 'Revertir' },
  aiSuggestionParticipantIdNotFound: {
    eng: (fieldLabel, id) =>
      `No contact found for ${fieldLabel} with ID ${id}. Check your sender/receiver lists or add this ID in management.`,
    esp: (fieldLabel, id) =>
      `No se encontró contacto para ${fieldLabel} con ID ${id}. Revisa la lista de remitentes/destinatarios o añade este ID en gestión.`,
  },
  aiSuggestionParticipantIdNotFoundBatch: {
    eng: (count) =>
      `${count} participant suggestions could not be matched to a contact by ID. Check sender, receiver, and agent lists.`,
    esp: (count) =>
      `${count} sugerencias de participantes no coinciden con ningún contacto por ID. Revisa las listas de remitente, destinatario y agente.`,
  },
  // Add more as needed
};
export function getMainTranslation(key, isSpanish, ...args) {
  if (!mainTranslations[key]) return key;
  const value = mainTranslations[key];
  if (typeof value.eng === 'function' && typeof value.esp === 'function') {
    return isSpanish ? value.esp(...args) : value.eng(...args);
  }
  return isSpanish ? value.esp : value.eng;
}

export const reviewExtractedDataTranslations = {
  extractedData: { eng: 'Extracted Data', esp: 'Datos extraídos' },
  collapse: { eng: 'Collapse', esp: 'Colapsar' },
  expand: { eng: 'Expand', esp: 'Expandir' },
  splitView: { eng: 'Split View', esp: 'Vista dividida' },
  uploadedFiles: { eng: 'Uploaded Files', esp: 'Archivos subidos' },
  noPdfFileSelected: { eng: 'No PDF File Selected', esp: 'Ningún archivo PDF seleccionado' },
  noFilesAvailable: { eng: 'No files available', esp: 'No hay archivos disponibles' },
  loadingPdfs: { eng: 'Loading PDFs...', esp: 'Cargando PDFs...' },
  selectAFileToView: { eng: 'Select a file to view', esp: 'Selecciona un archivo para ver' },
  close: { eng: 'Close', esp: 'Cerrar' },
  selectTemplate: { eng: 'Select a template', esp: 'Seleccionar una plantilla' },
  template: { eng: 'Template', esp: 'Plantilla' },
  selectTemplateTitle: { eng: 'Select Template', esp: 'Seleccionar Plantilla' },
};
export function getReviewExtractedDataTranslation(key, isSpanish) {
  if (!reviewExtractedDataTranslations[key]) return key;
  return isSpanish ? reviewExtractedDataTranslations[key].esp : reviewExtractedDataTranslations[key].eng;
}

export const uploadDocumentsTranslations = {
  uploadDocuments: { eng: 'Upload Documents', esp: 'Subir documentos' },
  uploadDescription: {
    eng: 'Upload customs documents (transport/CMR and export PDFs) to extract and process customs data',
    esp: 'Sube documentos de aduana (transportes/CMR y PDFs de exportación) para extraer y procesar datos aduaneros'
  },
  selectFiles: { eng: 'Select Files', esp: 'Seleccionar archivos' },
  processing: { eng: 'Processing...', esp: 'Procesando...' },
  documentsProcessed: { eng: 'Documents Processed', esp: 'Documentos procesados' },
  documentLimitReached: { eng: 'Document Limit Reached', esp: 'Límite de documentos alcanzado' },
  or: { eng: 'or', esp: 'o' },
  dragDrop: { eng: 'Drag & Drop your files here', esp: 'Arrastra y suelta tus archivos aquí' },
  documentsSuccessfullyProcessed: { eng: 'Documents successfully processed', esp: 'Documentos procesados correctamente' },
  pleaseRemoveSome: { eng: 'Please remove some documents to upload more', esp: 'Elimina algunos documentos para subir más' },
  uploadedFiles: { eng: 'Uploaded Files', esp: 'Archivos subidos' },
  fileName: { eng: 'File Name', esp: 'Nombre del archivo' },
  size: { eng: 'Size', esp: 'Tamaño' },
  delete: { eng: 'Delete', esp: 'Eliminar' },
  mb: { eng: 'mb', esp: 'mb' },
  maxDocumentsError: {
    eng: (max, adding, current) => `You can upload a maximum of ${max} documents. You're trying to add ${adding} files when you already have ${current}.`,
    esp: (max, adding, current) => `Puedes subir un máximo de ${max} documentos. Estás intentando añadir ${adding} archivos cuando ya tienes ${current}.`
  },
  errorProcessingFiles: {
    eng: 'Error processing the files. Please try again.',
    esp: 'Error al procesar los archivos. Por favor, inténtalo de nuevo.'
  },
  storageQuotaExceeded: {
    eng: 'Storage quota exceeded. Could not store the files. Please clear some space or use smaller files.',
    esp: 'Cuota de almacenamiento excedida. No se pudieron almacenar los archivos. Por favor, libera espacio o usa archivos más pequeños.'
  },
  someFilesStorageFailed: {
    eng: (failedNames) => `Some files could not be stored due to storage limits: ${failedNames}. Please clear some space or use smaller files.`,
    esp: (failedNames) => `Algunos archivos no se pudieron almacenar debido a límites de almacenamiento: ${failedNames}. Por favor, libera espacio o usa archivos más pequeños.`
  },
  noWorkflowSession: {
    eng: 'No active workflow session. Please refresh the page.',
    esp: 'No hay sesión de flujo de trabajo activa. Por favor, actualiza la página.'
  },
  errorStoringFiles: {
    eng: 'Error storing files in storage. Please try again.',
    esp: 'Error al almacenar archivos. Por favor, inténtalo de nuevo.'
  },
  noFilesStored: {
    eng: 'No files could be stored. Storage quota may be exceeded.',
    esp: 'No se pudieron almacenar archivos. La cuota de almacenamiento puede estar excedida.'
  }
};
export function getUploadDocumentsTranslation(key, isSpanish, ...args) {
  if (!uploadDocumentsTranslations[key]) return key;
  const value = uploadDocumentsTranslations[key];
  if (typeof value.eng === 'function') {
    return isSpanish ? value.esp(...args) : value.eng(...args);
  }
  return isSpanish ? value.esp : value.eng;
}

export const creditSnippetTranslations = {
  remainingCredits: { eng: 'Remaining Credits', esp: 'Créditos restantes' },
  usedToday: { eng: 'Used Today', esp: 'Usados hoy' },
  usedThisMonth: { eng: 'Used This Month', esp: 'Usados este mes' },
  credits: { eng: 'CREDITS', esp: 'CRÉDITOS' },
  export: { eng: 'EXPORT', esp: 'EXPORTACIÓN' },
  total: { eng: 'Total', esp: 'Total' },
  averageCost: { eng: 'Average Cost', esp: 'Costo promedio' },
  buyCredits: { eng: 'Buy Credits', esp: 'Comprar créditos' },
  loading: { eng: '...', esp: '...' },
  error: { eng: '!', esp: '!' },
};
export function getCreditSnippetTranslation(key, isSpanish) {
  if (!creditSnippetTranslations[key]) return key;
  return isSpanish ? creditSnippetTranslations[key].esp : creditSnippetTranslations[key].eng;
}

export const stripePaymentModalTranslations = {
  purchaseCredits: { eng: 'Purchase Credits', esp: 'Comprar créditos' },
  credits: { eng: 'credits', esp: 'créditos' },
  buy: { eng: 'Buy', esp: 'Comprar' },
  processing: { eng: 'Processing...', esp: 'Procesando...' },
};
export function getStripePaymentModalTranslation(key, isSpanish) {
  if (!stripePaymentModalTranslations[key]) return key;
  return isSpanish ? stripePaymentModalTranslations[key].esp : stripePaymentModalTranslations[key].eng;
}

export const sidebarTranslations = {
  home: { eng: 'Home', esp: 'Inicio' },
  userManagement: { eng: 'User Management', esp: 'Gestión de usuarios' },
  senderManagement: { eng: 'Sender Management', esp: 'Gestión de remitentes' },
  receiversManagement: { eng: 'Receivers Management', esp: 'Gestión de destinatarios' },
  customsAgent: { eng: 'Customs Agent', esp: 'Agente de aduanas' },
  declarationManagement: { eng: 'Declaration Management', esp: 'Gestión de declaraciones' },
  creditHistory: { eng: 'Credit History', esp: 'Historial de créditos' },
};
export function getSidebarTranslation(key, isSpanish) {
  if (!sidebarTranslations[key]) return key;
  return isSpanish ? sidebarTranslations[key].esp : sidebarTranslations[key].eng;
} 


export const userManagementTranslations = {
  userProfileManagement: { eng: 'User Profile Management', esp: 'Gestión de perfil de usuario' },
  userDetails: { eng: 'User Details', esp: 'Detalles del usuario' },
  companyDetails: { eng: 'Company Details', esp: 'Detalles de la empresa' },
  edit: { eng: 'Edit', esp: 'Editar' },
  cancel: { eng: 'Cancel', esp: 'Cancelar' },
  save: { eng: 'Save', esp: 'Guardar' },
  saving: { eng: 'Saving...', esp: 'Guardando...' },
  createCompany: { eng: 'Create Company', esp: 'Crear empresa' },
  creating: { eng: 'Creating...', esp: 'Creando...' },
  companyName: { eng: 'Company Name', esp: 'Nombre de la empresa' },
  companyEmail: { eng: 'Company Email', esp: 'Correo de la empresa' },
  nif: { eng: 'NIF', esp: 'NIF' },
  eoriNumber: { eng: 'EORI Number', esp: 'Número EORI' },
  address: { eng: 'Address', esp: 'Dirección' },
  city: { eng: 'City', esp: 'Ciudad' },
  postalCode: { eng: 'Postal Code', esp: 'Código Postal' },
  country: { eng: 'Country', esp: 'País' },
  standardCustomsOffice: { eng: 'Standard Customs Office', esp: 'Aduana habitual' },
  firstName: { eng: 'First Name', esp: 'Nombre' },
  lastName: { eng: 'Last Name', esp: 'Apellido' },
  email: { eng: 'Email', esp: 'Correo electrónico' },
  errorUpdatingUser: { eng: 'Error updating user details!', esp: '¡Error al actualizar los detalles del usuario!' },
  errorUpdatingCompany: { eng: 'Error updating company details!', esp: '¡Error al actualizar los detalles de la empresa!' },
  errorCreatingCompany: { eng: 'Error creating company!', esp: '¡Error al crear la empresa!' },
  successUserUpdate: { eng: 'Successfully updated user details!', esp: '¡Detalles de usuario actualizados con éxito!' },
  successCompanyUpdate: { eng: 'Successfully updated company details!', esp: '¡Detalles de empresa actualizados con éxito!' },
  successCompanyCreate: { eng: 'Successfully created company!', esp: '¡Empresa creada con éxito!' },
};
export function getUserManagementTranslation(key, isSpanish) {
  if (!userManagementTranslations[key]) return key;
  return isSpanish ? userManagementTranslations[key].esp : userManagementTranslations[key].eng;
}

export const senderManagementTranslations = {
  senderInformationManagement: { eng: 'Sender Information Management', esp: 'Gestión de remitentes' },
  searchSenders: { eng: 'Search senders...', esp: 'Buscar remitentes...' },
  sortBy: { eng: 'Sort by', esp: 'Ordenar por' },
  nameAZ: { eng: 'Name (A-Z)', esp: 'Nombre (A-Z)' },
  nameZA: { eng: 'Name (Z-A)', esp: 'Nombre (Z-A)' },
  newestFirst: { eng: 'Newest First', esp: 'Más nuevos primero' },
  oldestFirst: { eng: 'Oldest First', esp: 'Más antiguos primero' },
  addSender: { eng: 'Add Sender', esp: 'Agregar remitente' },
  editSender: { eng: 'Edit Sender', esp: 'Editar remitente' },
  senderName: { eng: 'Sender Name', esp: 'Nombre del remitente' },
  senderTaxId: { eng: 'Sender Tax Identification', esp: 'Identificación Fiscal del remitente' },
  senderEori: { eng: 'Sender EORI', esp: 'EORI del remitente' },
  senderAddress: { eng: 'Sender Address', esp: 'Dirección del remitente' },
  senderCity: { eng: 'Sender City', esp: 'Ciudad del remitente' },
  senderCountry: { eng: 'Sender Country', esp: 'País del remitente' },
  processing: { eng: 'Processing...', esp: 'Procesando...' },
  updateSender: { eng: 'Update Sender', esp: 'Actualizar remitente' },
  cancel: { eng: 'Cancel', esp: 'Cancelar' },
  confirmDeletion: { eng: 'Confirm Deletion', esp: 'Confirmar eliminación' },
  areYouSureDelete: { eng: 'Are you sure you want to delete the sender', esp: '¿Estás seguro de que deseas eliminar el remitente' },
  thisActionCannotBeUndone: { eng: 'This action cannot be undone.', esp: 'Esta acción no se puede deshacer.' },
  deleting: { eng: 'Deleting...', esp: 'Eliminando...' },
  deleteSender: { eng: 'Delete Sender', esp: 'Eliminar remitente' },
  actions: { eng: 'Actions', esp: 'Acciones' },
  selectAction: { eng: 'Select Action', esp: 'Seleccionar acción' },
  edit: { eng: 'Edit', esp: 'Editar' },
  delete: { eng: 'Delete', esp: 'Eliminar' },
  noSendersFound: { eng: 'No senders found', esp: 'No se encontraron remitentes' },
  successDeleteSender: { eng: 'Successfully deleted sender!', esp: '¡Remitente eliminado con éxito!' },
  successUpdateSender: { eng: 'Successfully updated sender!', esp: '¡Remitente actualizado con éxito!' },
  successCreateSender: { eng: 'Successfully created a new sender!', esp: '¡Nuevo remitente creado con éxito!' },
  errorUpdateSender: { eng: 'Error updating sender!', esp: '¡Error al actualizar el remitente!' },
  errorCreateSender: { eng: 'Error creating a new sender!', esp: '¡Error al crear un nuevo remitente!' },
};
export function getSenderManagementTranslation(key, isSpanish) {
  if (!senderManagementTranslations[key]) return key;
  return isSpanish ? senderManagementTranslations[key].esp : senderManagementTranslations[key].eng;
} 

export const receiverManagementTranslations = {
  receiverInformationManagement: { eng: 'Receiver Information Management', esp: 'Gestión de destinatarios' },
  searchReceivers: { eng: 'Search receivers...', esp: 'Buscar destinatarios...' },
  sortBy: { eng: 'Sort by', esp: 'Ordenar por' },
  nameAZ: { eng: 'Name (A-Z)', esp: 'Nombre (A-Z)' },
  nameZA: { eng: 'Name (Z-A)', esp: 'Nombre (Z-A)' },
  newestFirst: { eng: 'Newest First', esp: 'Más nuevos primero' },
  oldestFirst: { eng: 'Oldest First', esp: 'Más antiguos primero' },
  addReceiver: { eng: 'Add Receiver', esp: 'Agregar destinatario' },
  editReceiver: { eng: 'Edit Receiver', esp: 'Editar destinatario' },
  receiverName: { eng: 'Receiver Name', esp: 'Nombre del destinatario' },
  receiverTaxId: { eng: 'Receiver Tax ID', esp: 'NIF del destinatario' },
  receiverEori: { eng: 'Receiver EORI', esp: 'EORI del destinatario' },
  receiverAddress: { eng: 'Receiver Address', esp: 'Dirección del destinatario' },
  receiverCity: { eng: 'Receiver City', esp: 'Ciudad del destinatario' },
  receiverCountry: { eng: 'Receiver Country', esp: 'País del destinatario' },
  processing: { eng: 'Processing...', esp: 'Procesando...' },
  updateReceiver: { eng: 'Update Receiver', esp: 'Actualizar destinatario' },
  cancel: { eng: 'Cancel', esp: 'Cancelar' },
  confirmDeletion: { eng: 'Confirm Deletion', esp: 'Confirmar eliminación' },
  areYouSureDelete: { eng: 'Are you sure you want to delete the receiver', esp: '¿Estás seguro de que deseas eliminar el destinatario' },
  thisActionCannotBeUndone: { eng: 'This action cannot be undone.', esp: 'Esta acción no se puede deshacer.' },
  deleting: { eng: 'Deleting...', esp: 'Eliminando...' },
  deleteReceiver: { eng: 'Delete Receiver', esp: 'Eliminar destinatario' },
  actions: { eng: 'Actions', esp: 'Acciones' },
  selectAction: { eng: 'Select Action', esp: 'Seleccionar acción' },
  edit: { eng: 'Edit', esp: 'Editar' },
  delete: { eng: 'Delete', esp: 'Eliminar' },
  noReceiversFound: { eng: 'No receivers found', esp: 'No se encontraron destinatarios' },
  successDeleteReceiver: { eng: 'Successfully deleted receiver!', esp: '¡Destinatario eliminado con éxito!' },
  successUpdateReceiver: { eng: 'Successfully updated receiver!', esp: '¡Destinatario actualizado con éxito!' },
  successCreateReceiver: { eng: 'Successfully created a new receiver!', esp: '¡Nuevo destinatario creado con éxito!' },
  errorUpdateReceiver: { eng: 'Error updating receiver!', esp: '¡Error al actualizar el destinatario!' },
  errorCreateReceiver: { eng: 'Error creating a new receiver!', esp: '¡Error al crear un nuevo destinatario!' },
};

export function getReceiverManagementTranslation(key, isSpanish) {
  if (!receiverManagementTranslations[key]) return key;
  return isSpanish ? receiverManagementTranslations[key].esp : receiverManagementTranslations[key].eng;
}

export const customsAgentManagementTranslations = {
  customsAgentInformationManagement: { eng: 'Customs Agent Information Management', esp: 'Gestión de agentes de aduanas' },
  searchAgents: { eng: 'Search agents...', esp: 'Buscar agentes...' },
  sortBy: { eng: 'Sort by', esp: 'Ordenar por' },
  nameAZ: { eng: 'Name (A-Z)', esp: 'Nombre (A-Z)' },
  nameZA: { eng: 'Name (Z-A)', esp: 'Nombre (Z-A)' },
  newestFirst: { eng: 'Newest First', esp: 'Más nuevos primero' },
  oldestFirst: { eng: 'Oldest First', esp: 'Más antiguos primero' },
  addAgent: { eng: 'Add Agent', esp: 'Agregar agente' },
  editAgent: { eng: 'Edit Agent', esp: 'Editar agente' },
  agentName: { eng: 'Agent Name', esp: 'Nombre del agente' },
  agentTaxId: { eng: 'Agent Tax ID', esp: 'NIF del agente' },
  agentEori: { eng: 'Agent EORI', esp: 'EORI del agente' },
  agentAddress: { eng: 'Agent Address', esp: 'Dirección del agente' },
  agentCity: { eng: 'Agent City', esp: 'Ciudad del agente' },
  agentCountry: { eng: 'Agent Country', esp: 'País del agente' },
  processing: { eng: 'Processing...', esp: 'Procesando...' },
  updateAgent: { eng: 'Update Agent', esp: 'Actualizar agente' },
  cancel: { eng: 'Cancel', esp: 'Cancelar' },
  confirmDeletion: { eng: 'Confirm Deletion', esp: 'Confirmar eliminación' },
  areYouSureDelete: { eng: 'Are you sure you want to delete the agent', esp: '¿Estás seguro de que deseas eliminar el agente' },
  thisActionCannotBeUndone: { eng: 'This action cannot be undone.', esp: 'Esta acción no se puede deshacer.' },
  deleting: { eng: 'Deleting...', esp: 'Eliminando...' },
  deleteAgent: { eng: 'Delete Agent', esp: 'Eliminar agente' },
  actions: { eng: 'Actions', esp: 'Acciones' },
  selectAction: { eng: 'Select Action', esp: 'Seleccionar acción' },
  edit: { eng: 'Edit', esp: 'Editar' },
  delete: { eng: 'Delete', esp: 'Eliminar' },
  noAgentsFound: { eng: 'No agents found', esp: 'No se encontraron agentes' },
  successDeleteAgent: { eng: 'Successfully deleted agent!', esp: '¡Agente eliminado con éxito!' },
  successUpdateAgent: { eng: 'Successfully updated agent!', esp: '¡Agente actualizado con éxito!' },
  successCreateAgent: { eng: 'Successfully created a new agent!', esp: '¡Nuevo agente creado con éxito!' },
  errorUpdateAgent: { eng: 'Error updating agent!', esp: '¡Error al actualizar el agente!' },
  errorCreateAgent: { eng: 'Error creating a new agent!', esp: '¡Error al crear un nuevo agente!' },
};
export function getCustomsAgentManagementTranslation(key, isSpanish) {
  if (!customsAgentManagementTranslations[key]) return key;
  return isSpanish ? customsAgentManagementTranslations[key].esp : customsAgentManagementTranslations[key].eng;
}

export const declarationManagementTranslations = {
  exportDeclarationManagement: { eng: 'Export Declaration Management', esp: 'Gestión de declaraciones de exportación' },
  declarationManagement: { eng: 'Declaration Management', esp: 'Gestión de declaraciones' },
  exports: { eng: 'Exports', esp: 'Exportaciones' },
  imports: { eng: 'Imports', esp: 'Importaciones' },
  declarationsThisMonth: { eng: 'Declarations This Month', esp: 'Declaraciones este mes' },
  topDestinationCountry: { eng: 'Top Destination Country', esp: 'País destino más frecuente' },
  searchByExpedienteOrFactura: { eng: 'Search by Expediente or Factura...', esp: 'Buscar por Expediente o Factura...' },
  searchByExpedienteOrPais: { eng: 'Search by expediente or country', esp: 'Buscar por expediente o país' },
  sortBy: { eng: 'Sort by', esp: 'Ordenar por' },
  expedienteAZ: { eng: 'Expediente (A-Z)', esp: 'Expediente (A-Z)' },
  expedienteZA: { eng: 'Expediente (Z-A)', esp: 'Expediente (Z-A)' },
  idAsc: { eng: 'ID (Low to High)', esp: 'ID (Menor a Mayor)' },
  idDesc: { eng: 'ID (High to Low)', esp: 'ID (Mayor a Menor)' },
  newestFirst: { eng: 'Newest First', esp: 'Más nuevos primero' },
  oldestFirst: { eng: 'Oldest First', esp: 'Más antiguos primero' },
  // DeclarationsTable translations:
  id: { eng: 'ID', esp: 'ID' },
  expediente: { eng: 'Expediente', esp: 'Expediente' },
  factura: { eng: 'Factura', esp: 'Factura' },
  fechaFactura: { eng: 'Fecha Factura', esp: 'Fecha Factura' },
  fechaCreacion: { eng: 'Fecha Creación', esp: 'Fecha Creación' },
  totalBultos: { eng: 'Total Bultos', esp: 'Total Bultos' },
  paisDestino: { eng: 'País Destino', esp: 'País Destino' },
  actions: { eng: 'Actions', esp: 'Acciones' },
  selectAction: { eng: 'Select Action', esp: 'Seleccionar acción' },
  view: { eng: 'View', esp: 'Ver' },
  edit: { eng: 'Edit', esp: 'Editar' },
  exportToTaric: { eng: 'Export to Taric', esp: 'Exportar a Taric' },
  importToTaric: { eng: 'Import to Taric', esp: 'Importar a Taric' },
  noDeclarationsFound: { eng: 'No declarations found', esp: 'No se encontraron declaraciones' },
  jsonFile: { eng: 'JSON File', esp: 'Archivo JSON' },
};
export function getDeclarationManagementTranslation(key, isSpanish) {
  if (!declarationManagementTranslations[key]) return key;
  return isSpanish ? declarationManagementTranslations[key].esp : declarationManagementTranslations[key].eng;
}

export const declarationViewTranslations = {
  declarationDetails: { eng: 'Declaration Details', esp: 'Detalles de la Declaración' },
  expand: { eng: 'Expand', esp: 'Expandir' },
  collapse: { eng: 'Collapse', esp: 'Colapsar' },
  splitView: { eng: 'Split View', esp: 'Vista dividida' },
  uploadedFiles: { eng: 'Uploaded Files', esp: 'Archivos subidos' },
  noPdfFileSelected: { eng: 'No PDF File Selected', esp: 'Ningún archivo PDF seleccionado' },
  noFilesAvailable: { eng: 'No files available', esp: 'No hay archivos disponibles' },
  close: { eng: 'Close', esp: 'Cerrar' },
  selectAFileToView: { eng: 'Select a file to view', esp: 'Selecciona un archivo para ver' },
};
export function getDeclarationViewTranslation(key, isSpanish) {
  if (!declarationViewTranslations[key]) return key;
  return isSpanish ? declarationViewTranslations[key].esp : declarationViewTranslations[key].eng;
}

export const loginTranslations = {
  loginToCustomFlows: { eng: 'Log in to CargoFlow', esp: 'Iniciar sesión en CargoFlow' },
  email: { eng: 'Email', esp: 'Correo electrónico' },
  password: { eng: 'Password', esp: 'Contraseña' },
  loggingIn: { eng: 'Logging in...', esp: 'Iniciando sesión...' },
  logIn: { eng: 'Log in', esp: 'Iniciar sesión' },
  emailAndPasswordRequired: { eng: 'Email and password are required', esp: 'Correo electrónico y contraseña son obligatorios' },
  successfullyLoggedIn: { eng: 'Successfully logged in!', esp: '¡Inicio de sesión exitoso!' },
  couldNotRetrieveUser: { eng: 'Could not retrieve user information. Please try again.', esp: 'No se pudo obtener la información del usuario. Por favor, inténtalo de nuevo.' },
  authenticationFailed: { eng: 'Authentication failed!', esp: '¡Autenticación fallida!' },
  errorOccurred: { eng: 'An error occurred. Please try again later.', esp: 'Ocurrió un error. Por favor, inténtalo de nuevo más tarde.' },
  // Add more as needed
};
export function getLoginTranslation(key, isSpanish) {
  if (!loginTranslations[key]) return key;
  return isSpanish ? loginTranslations[key].esp : loginTranslations[key].eng;
}

export const successPageTranslations = {
  loadingPaymentStatus: {
    eng: 'Loading payment status...',
    esp: 'Cargando estado del pago...'
  },
  noSessionId: {
    eng: 'No session ID found.',
    esp: 'No se encontró el ID de la sesión.'
  },
  noPaymentFound: {
    eng: 'No payment found.',
    esp: 'No se encontró el pago.'
  },
  paymentSuccessful: {
    eng: 'Payment Successful!',
    esp: '¡Pago realizado con éxito!'
  },
  thankYou: {
    eng: 'Thank you for your purchase.',
    esp: 'Gracias por su compra.'
  },
  product: {
    eng: 'Product',
    esp: 'Producto'
  },
  credits: {
    eng: 'credits',
    esp: 'créditos'
  },
  amount: {
    eng: 'Amount',
    esp: 'Importe'
  },
  status: {
    eng: 'Status',
    esp: 'Estado'
  },
  date: {
    eng: 'Date',
    esp: 'Fecha'
  },
  goToHome: {
    eng: 'Go to Home',
    esp: 'Ir al inicio'
  },
};

export function getSuccessPageTranslation(key, isSpanish) {
  if (!successPageTranslations[key]) return key;
  return isSpanish ? successPageTranslations[key].esp : successPageTranslations[key].eng;
}

export const paymentsPageTranslations = {
  loadingPayments: {
    eng: 'Loading payments...',
    esp: 'Cargando pagos...'
  },
  userNotLoggedIn: {
    eng: 'User not logged in.',
    esp: 'Usuario no ha iniciado sesión.'
  },
  failedToFetchPayments: {
    eng: 'Failed to fetch payments',
    esp: 'No se pudieron obtener los pagos'
  },
  myCreditHistory: {
    eng: 'My Credit History',
    esp: 'Mi historial de créditos'
  },
  searchCreditHistory: {
    eng: 'Search credit history...',
    esp: 'Buscar historial de créditos...'
  },
  sort: {
    eng: 'Sort',
    esp: 'Ordenar'
  },
  newestFirst: {
    eng: 'Newest First',
    esp: 'Más nuevos primero'
  },
  oldestFirst: {
    eng: 'Oldest First',
    esp: 'Más antiguos primero'
  },
  amountHighLow: {
    eng: 'Amount High-Low',
    esp: 'Importe mayor-menor'
  },
  amountLowHigh: {
    eng: 'Amount Low-High',
    esp: 'Importe menor-mayor'
  },
  product: {
    eng: 'Product',
    esp: 'Producto'
  },
  amount: {
    eng: 'Amount',
    esp: 'Importe'
  },
  currency: {
    eng: 'Currency',
    esp: 'Moneda'
  },
  status: {
    eng: 'Status',
    esp: 'Estado'
  },
  date: {
    eng: 'Date',
    esp: 'Fecha'
  },
  credits: {
    eng: 'credits',
    esp: 'créditos'
  },
  noPaymentsFound: {
    eng: 'No payments found.',
    esp: 'No se encontraron pagos.'
  },
};

export function getPaymentsPageTranslation(key, isSpanish) {
  if (!paymentsPageTranslations[key]) return key;
  return isSpanish ? paymentsPageTranslations[key].esp : paymentsPageTranslations[key].eng;
}